/**
 * @module builder
 */

import type { PresentationBlock, PresentationOptionV2, PresentationPageV2, PresentationSectionV2 } from 'paintscout'

/**
 * Shared lookups for the v2 content tree (`pages[] → sections[] → blocks[]`).
 *
 * Everything in this module addresses tree nodes by **key**, not by index path. The v1 helpers took
 * `{ pageIndex, sectionIndex }` pairs, which silently target the wrong node whenever the caller's
 * indices are stale (a reorder or delete between render and click) — keys either resolve or don't.
 */

export interface PageLocation {
  page: PresentationPageV2
  pageIndex: number
}

export interface SectionLocation extends PageLocation {
  section: PresentationSectionV2
  sectionIndex: number
}

export interface BlockLocation extends SectionLocation {
  block: PresentationBlock
  blockIndex: number
}

export function findPage(presentation: PresentationOptionV2, pageKey: string): PageLocation | null {
  const pageIndex = presentation?.pages?.findIndex((page) => page.key === pageKey) ?? -1

  return pageIndex === -1 ? null : { page: presentation.pages[pageIndex], pageIndex }
}

/** Sections are unique across the whole presentation, so no page key is needed to find one. */
export function findSection(presentation: PresentationOptionV2, sectionKey: string): SectionLocation | null {
  const pages = presentation?.pages ?? []

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex]
    const sectionIndex = page.sections?.findIndex((section) => section.key === sectionKey) ?? -1

    if (sectionIndex !== -1) {
      return { page, pageIndex, section: page.sections[sectionIndex], sectionIndex }
    }
  }

  return null
}

/** Blocks are unique across the whole presentation, so no page/section key is needed to find one. */
export function findBlock(presentation: PresentationOptionV2, blockKey: string): BlockLocation | null {
  const pages = presentation?.pages ?? []

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
    const page = pages[pageIndex]
    const sections = page.sections ?? []

    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
      const section = sections[sectionIndex]
      const blockIndex = section.blocks?.findIndex((block) => block.key === blockKey) ?? -1

      if (blockIndex !== -1) {
        return {
          page,
          pageIndex,
          section,
          sectionIndex,
          block: section.blocks[blockIndex],
          blockIndex
        }
      }
    }
  }

  return null
}

/** Replaces one page, cloning only along the path that changed. */
export function withPages(presentation: PresentationOptionV2, pages: PresentationPageV2[]): PresentationOptionV2 {
  return { ...presentation, pages }
}

export function replaceAt<T>(items: T[], index: number, item: T): T[] {
  const next = [...items]
  next.splice(index, 1, item)
  return next
}

export function insertAt<T>(items: T[], index: number, item: T): T[] {
  const next = [...items]
  next.splice(clampIndex(index, items.length), 0, item)
  return next
}

export function removeAt<T>(items: T[], index: number): T[] {
  const next = [...items]
  next.splice(index, 1)
  return next
}

export function moveWithin<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const next = [...items]
  const [moved] = next.splice(fromIndex, 1)
  next.splice(clampIndex(toIndex, next.length), 0, moved)
  return next
}

/** An out-of-range destination appends rather than producing a sparse array. */
function clampIndex(index: number, length: number): number {
  if (typeof index !== 'number' || index < 0 || index > length) {
    return length
  }
  return index
}
